/**
 * QUANTITÉS (05/11/2026) — répartition d'une quantité voulue sur des lots, côté bureau (copie de apps/terrain/src/lib/repartition.ts).
 *
 * Copie fidèle de `repartirFifo` / `arrondi2` / `quantiteValide` de apps/api/src/modules/operations/beton/bl-terrain.rules.ts (le serveur
 * REVÉRIFIE tout à l'émission : ce module ne fait que proposer). Un essai de l'API compare les deux versions sur les mêmes cas
 * (apps/api/src/modules/operations/beton/repartition-portail.spec.ts) : si l'une change sans l'autre, l'essai échoue.
 */

export interface LotRepartissable { id: string; lotNumber: string; quantite: number; dateProduction: Date | string }
export interface LigneChoisie { lotId: string; quantite: number }

export const arrondi2 = (n: number): number => Math.round(n * 100) / 100;

/** Une quantité de pièces : strictement positive, deux décimales au plus. */
export const quantiteValide = (q: unknown): q is number =>
  typeof q === 'number' && Number.isFinite(q) && q > 0 && Math.abs(q * 100 - Math.round(q * 100)) < 1e-6;

const dansLOrdre = <T extends { lotNumber: string; dateProduction: Date | string }>(lots: T[]): T[] =>
  [...lots].sort((a, b) => {
    const da = new Date(a.dateProduction).getTime();
    const db = new Date(b.dateProduction).getTime();
    return da !== db ? da - db : a.lotNumber.localeCompare(b.lotNumber);
  });

/** Lots les plus anciens d'abord, entiers, puis une PARTIE du dernier pour tomber exactement sur la quantité voulue. */
export function repartirFifo(
  lots: LotRepartissable[],
  quantiteVoulue: number,
  disponible: (lot: LotRepartissable) => number = (l) => l.quantite,
): { lignes: LigneChoisie[]; manque: number } {
  const lignes: LigneChoisie[] = [];
  let reste = arrondi2(quantiteVoulue);
  for (const lot of dansLOrdre(lots)) {
    if (reste <= 0) break;
    const dispo = arrondi2(disponible(lot));
    if (dispo <= 0) continue;
    const pris = Math.min(dispo, reste);
    lignes.push({ lotId: lot.id, quantite: arrondi2(pris) });
    reste = arrondi2(reste - pris);
  }
  return { lignes, manque: Math.max(0, reste) };
}

/**
 * Ramène un choix qui DÉPASSE la quantité voulue à cette quantité : on retire l'excédent en commençant par les lots les plus RÉCENTS
 * (les plus anciens restent pris). Un lot ramené à zéro n'est plus choisi.
 */
export function retirerExcedent(
  choix: Array<LotRepartissable & { prise: number }>,
  quantiteVoulue: number,
): Array<{ lotId: string; quantite: number }> {
  let excedent = arrondi2(choix.reduce((n, c) => n + c.prise, 0) - quantiteVoulue);
  const resultat = new Map<string, number>(choix.map((c) => [c.id, c.prise]));
  for (const c of dansLOrdre(choix).reverse()) {
    if (excedent <= 0) break;
    const retire = Math.min(c.prise, excedent);
    const garde = arrondi2(c.prise - retire);
    if (garde <= 0) resultat.delete(c.id); else resultat.set(c.id, garde);
    excedent = arrondi2(excedent - retire);
  }
  return [...resultat.entries()].map(([lotId, quantite]) => ({ lotId, quantite }));
}
